/**
 * Command types and interfaces for the modular command system
 */

import type { KeyboardShortcutId } from '@/lib/keyboard-shortcuts/registry';
import type { ReadonlyURLSearchParams } from 'next/navigation';
import type { ReactNode } from 'react';

/**
 * Command group constants with priority values for consistent group ordering
 * Higher priority values appear first in the command menu
 */
export const COMMAND_GROUPS = {
  GENERAL: {
    name: 'General',
    priority: 900,
  },
  NAVIGATION: {
    name: 'Navigation',
    priority: 800,
  },
  SETTINGS: {
    name: 'Settings',
    priority: 780,
  },
  AI_REVIEW_SETTINGS: {
    name: 'AI review settings',
    priority: 750,
  },
  PULL_REQUESTS: {
    name: 'Pull requests',
    priority: 1100,
  },
  PR_COMMANDS: {
    name: 'This pull request',
    priority: 1300,
  },
  INBOX_PULL_REQUEST: {
    name: 'Selected pull request',
    priority: 1300,
  },
  DIFF_LAYOUT: {
    name: 'Layout',
    priority: 690,
  },
  DIFF_OVERFLOW: {
    name: 'Overflow',
    priority: 680,
  },
  DIFF_INDICATORS: {
    name: 'Indicators',
    priority: 670,
  },
  DIFF_INLINE: {
    name: 'Inline diff',
    priority: 660,
  },
  DIFF_HUNK_HEADER: {
    name: 'Hunk header',
    priority: 650,
  },
  DIFF_FILES: {
    name: 'Files',
    priority: 640,
  },
  REVIEWERS: {
    name: 'Reviewers',
    priority: 1260,
  },
  TEAMS: {
    name: 'Teams',
    priority: 1250,
  },
  CLIPBOARD: {
    name: 'Clipboard',
    priority: 1400,
  },
  OTHER: {
    name: 'Other',
    priority: 0,
  },
} as const;

/**
 * Type for command group names
 */
export type CommandGroupName = (typeof COMMAND_GROUPS)[keyof typeof COMMAND_GROUPS]['name'];

export type CommandTag =
  | 'pr-search'
  | 'pr-search-loading'
  | 'pr-search-pending'
  | 'pr-search-empty'
  | 'pr-v2-add-reviewer'
  | 'pr-v2-add-reviewer-loading'
  | 'pr-v2-add-reviewer-empty'
  | 'pr-v2-diff-view'
  | 'pr-v2-review'
  | 'pr-v2-merge'
  | 'pr-v2-merge-empty'
  | 'pr-v2-compare'
  | 'pr-v2-file'
  | 'pr-v2-file-empty'
  | 'pr-v2-finding'
  | 'inbox-snooze';

export type PaletteModeId =
  | 'pr-search'
  | 'pr-v2-add-reviewer'
  | 'pr-v2-diff-view'
  | 'pr-v2-review'
  | 'pr-v2-merge'
  | 'pr-v2-compare'
  | 'pr-v2-files'
  | 'pr-v2-findings'
  | 'inbox-snooze';

/**
 * A palette "mode" is a one-level sub-page of the command palette. While a mode
 * is active, only commands tagged with its itemTag render, the input shows
 * badgeLabel + inputPlaceholder, and Escape, or Backspace on an empty search, returns to root.
 */
export type PaletteMode = {
  id: PaletteModeId;
  badgeLabel: string;
  inputPlaceholder: string;
  itemTag: CommandTag;
  loadingTag?: CommandTag;
  pendingTag?: CommandTag;
  emptyTag?: CommandTag;
  emptyMessage: string;
  triggerKeys?: readonly string[];
  prefixRegExp?: RegExp;
  clearOnRouteChange?: boolean;
  // Where the mode's items show up when the user searches from the root list. Unset keeps them
  // inside the mode.
  rootSearch?: 'commands' | 'pull-requests';
  // Rows show their number, 1 to 9 in browse order, and typing a number alone picks its row.
  numbered?: true;
};

/**
 * What a pull request row shows. Rows built from this render the title, state, author, repository
 * and age instead of `label`.
 */
export type CommandPullRequest = {
  owner: string;
  repo: string;
  number: number;
  title: string;
  state: 'open' | 'draft' | 'merged' | 'closed';
  reviewState: 'approved' | 'changes-requested' | 'review-required' | null;
  authorLogin: string | null;
  authorAvatarUrl: string | null;
  updatedAt: string | null;
  // The PR is in the inbox's Needs you section (the persisted field keeps its old name).
  needsReview: boolean;
};

export type Command = {
  id: string;
  label: string | ReactNode;
  displayLabel?: ReactNode;
  icon?: ReactNode;
  keywords?: string[];
  description?: string;
  examples?: string[];
  safeExecution?: 'navigation';
  // Id of the KEYBOARD_SHORTCUTS entry whose keys the row shows. Display only: it does not
  // register a hotkey.
  shortcutId?: KeyboardShortcutId;

  // The group this command belongs to (for UI organization)
  group?: CommandGroupName;

  // Heads the page's context group or a mode's list in place of the group name, such as the
  // selected pull request's number. It is display only, so ranking and analytics keep the fixed
  // group name.
  groupHeading?: string;

  // Priority for ordering commands (higher numbers appear first)
  priority?: number;

  // The action to execute when the command is triggered
  action: () => void | false | Promise<void>;

  // For conditional visibility based on URL path.
  // Strings match the exact route or nested routes. RegExp values test the pathname.
  showOn?:
    | string
    | RegExp
    | ((pathname: string, searchParams?: ReadonlyURLSearchParams) => boolean);

  // Tag for filtering commands (e.g. 'pr-search' for "/" prefix mode)
  tag?: CommandTag;

  // Selecting this command switches the palette into the named one-level mode.
  enterMode?: PaletteModeId;

  // Keep the palette open and preserve the search after action execution.
  keepOpen?: boolean;

  // Hide until the user types a search, for commands too many to list, like single settings.
  searchOnly?: boolean;

  // Option rows in a mode (the current diff layout, a requested reviewer) show a check mark.
  checked?: boolean;

  pullRequest?: CommandPullRequest;
};

/**
 * Command group type for grouping related commands with a title
 */
export type CommandGroup = {
  title: string;
  commands: Command[];
  // Priority for ordering command groups (higher numbers appear first)
  priority?: number;
};

/**
 * Command or CommandGroup type for flexible command organization
 */
export type CommandItem = Command | CommandGroup;
